import React, { useState } from 'react';
import { getFirestore, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { getSafeDisplayName } from '../utils/profanityFilter';

export default function FeedbackLink({ user, darkMode }) {
  const [showModal, setShowModal] = useState(false);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const db = getFirestore();

  const handleSubmit = async () => {
    if (rating === 0) {
      alert('Please select a rating');
      return;
    }

    setSubmitting(true);

    try {
      await addDoc(collection(db, 'generalFeedback'), {
        userId: user?.uid || 'anonymous',
        userEmail: user?.email || 'anonymous',
        userName: getSafeDisplayName(user?.displayName) || 'Anonymous User',
        rating: rating,
        comment: comment,
        timestamp: serverTimestamp(),
        reviewed: false
      });

      setSubmitted(true);
      setTimeout(() => {
        setShowModal(false);
        setSubmitted(false);
        setRating(0);
        setComment('');
      }, 2000);
    } catch (error) {
      console.error('Error submitting feedback:', error);
      alert('Failed to submit feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const colors = {
    dark: {
      overlay: 'rgba(0, 0, 0, 0.7)',
      modal: '#1e293b',
      text: '#ffffff',
      subtext: '#94a3b8',
      border: '#334155',
      accent: '#818cf8',
      button: '#818cf8',
      buttonHover: '#6366f1'
    },
    light: {
      overlay: 'rgba(0, 0, 0, 0.5)',
      modal: '#ffffff',
      text: '#1e293b',
      subtext: '#64748b',
      border: '#e2e8f0',
      accent: '#8b5cf6',
      button: '#8b5cf6',
      buttonHover: '#7c3aed'
    }
  };

  const theme = darkMode ? colors.dark : colors.light;

  return (
    <>
      {/* Floating Feedback Button - Always visible on top */}
      <button
        onClick={() => setShowModal(true)}
        className="floating-feedback-btn"
        title="Share Feedback"
        style={{
          position: 'fixed',
          bottom: '24px',
          right: '24px',
          background: 'linear-gradient(135deg, #818cf8 0%, #a78bfa 100%)',
          border: 'none',
          borderRadius: '50px',
          cursor: 'pointer',
          padding: '14px 24px',
          fontSize: '15px',
          fontWeight: '600',
          color: 'white',
          boxShadow: '0 8px 24px rgba(129, 140, 248, 0.4)',
          zIndex: 999998,
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          transition: 'all 0.3s ease',
          transform: showModal ? 'scale(0.95)' : 'scale(1)'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'scale(1.05) translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 12px 32px rgba(129, 140, 248, 0.5)';
        }}
        onMouseLeave={(e) => {
          if (!showModal) {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = '0 8px 24px rgba(129, 140, 248, 0.4)';
          }
        }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
        </svg>
        <span>Feedback</span>
      </button>

      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: theme.overlay,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 999999,
          padding: '20px',
          overflowY: 'auto'
        }}
        onClick={() => setShowModal(false)}
        >
          <div 
            onClick={(e) => e.stopPropagation()}
            style={{
            background: theme.modal,
            borderRadius: '16px',
            padding: '32px',
            maxWidth: '480px',
            width: '100%',
            boxShadow: '0 25px 75px rgba(0,0,0,0.5)',
            color: theme.text,
            margin: 'auto',
            position: 'relative',
            zIndex: 1000000
          }}>
            {!submitted ? (
              <>
                <h2 style={{ margin: '0 0 8px 0', fontSize: '24px', fontWeight: '600' }}>
                  Share Your Feedback
                </h2>
                <p style={{ margin: '0 0 24px 0', fontSize: '14px', color: theme.subtext }}>
                  Help us improve Fidora! Your feedback is invaluable.
                </p>

                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', marginBottom: '12px', fontWeight: '500' }}>
                    How would you rate your experience?
                  </label>
                  <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                    {[1, 2, 3, 4, 5].map(star => (
                      <button
                        key={star}
                        onClick={() => setRating(star)}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '36px',
                          transition: 'transform 0.2s',
                          transform: rating >= star ? 'scale(1.1)' : 'scale(1)'
                        }}
                      >
                        {rating >= star ? '⭐' : '☆'}
                      </button>
                    ))}
                  </div>
                  {rating > 0 && (
                    <p style={{ textAlign: 'center', marginTop: '8px', fontSize: '12px', color: theme.subtext }}>
                      {rating === 1 ? 'Poor' : rating === 2 ? 'Fair' : rating === 3 ? 'Good' : rating === 4 ? 'Very Good' : 'Excellent'}
                    </p>
                  )}
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>
                    Tell us more (optional)
                  </label>
                  <textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="What did you like? What can we improve?"
                    rows={4}
                    style={{
                      width: 'calc(100% - 24px)',
                      padding: '12px',
                      borderRadius: '8px',
                      border: `1px solid ${theme.border}`,
                      background: darkMode ? '#0f172a' : '#f8fafc',
                      color: theme.text,
                      fontSize: '14px',
                      resize: 'vertical',
                      fontFamily: 'inherit',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    onClick={() => setShowModal(false)}
                    style={{
                      flex: 1,
                      padding: '12px',
                      borderRadius: '8px',
                      border: `1px solid ${theme.border}`,
                      background: 'transparent',
                      color: theme.text,
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={submitting || rating === 0}
                    style={{
                      flex: 1,
                      padding: '12px',
                      borderRadius: '8px',
                      border: 'none',
                      background: rating === 0 ? theme.border : theme.button,
                      color: 'white',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: rating === 0 ? 'not-allowed' : 'pointer',
                      opacity: submitting ? 0.7 : 1
                    }}
                  >
                    {submitting ? 'Submitting...' : 'Submit'}
                  </button>
                </div>
              </>
            ) : (
              <div style={{ 
                textAlign: 'center', 
                padding: '40px 20px'
              }}>
                <div style={{ 
                  fontSize: '64px', 
                  marginBottom: '20px',
                  background: 'linear-gradient(135deg, #818cf8 0%, #a78bfa 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>
                  ✓
                </div>
                <h3 style={{ 
                  margin: '0 0 12px 0', 
                  fontSize: '24px', 
                  fontWeight: '600',
                  color: theme.text
                }}>
                  Thank You
                </h3>
                <p style={{ 
                  margin: 0, 
                  fontSize: '15px', 
                  color: theme.subtext,
                  lineHeight: 1.5
                }}>
                  Your feedback helps us improve Fidora.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

